// ══════════════════════════════════════════════════════════════════════════════════════════════
//  LA COLA DE ENVIOS DEL DISPOSITIVO: LO QUE NO LLEGO SE MANDA SOLO AL VOLVER LA CONEXION (B-1026, 3-oct-2026)
// ══════════════════════════════════════════════════════════════════════════════════════════════
//
//  El dueño: «si un doctor está mandando una nota de audio o un periodontograma y se cae el
//  servidor, que se "guarde" para enviarse al volver todo a la normalidad y no haya que rehacerla».
//
//  COMO FUNCIONA. Cada envio (la nota de voz de una cita, un dictado del periodontograma, un guardado
//  de sus medidas) se GUARDA PRIMERO en el dispositivo (IndexedDB: aguanta recargar la pagina y
//  cerrar el navegador) y despues se manda. Si llega, se borra de la cola. Si el servidor o la red no
//  contestan, se queda y se reintenta solo, cada vez mas espaciado, y la pantalla lo dice
//  («1 envío pendiente: se mandará solo», components/AvisoEnviosPendientes.jsx).
//
//  SIN DUPLICAR. Cada envio lleva su identificador unico desde que se guarda (`X-Envio-Id`), el MISMO
//  en todos sus intentos. El servidor lo reconoce (backend/middleware/envioUnico.js): si el primer
//  intento SI llego y lo que se perdio fue la respuesta, el reintento no lo vuelve a guardar.
//
//  NUNCA A LA CLINICA NI AL PACIENTE EQUIVOCADOS. Cada envio apunta, al guardarse, la CUENTA y la
//  CLINICA de la sesion, y su destino (la cita o el estudio concretos, no «el paciente abierto»).
//  Antes de mandar se compara con la sesion de ESE momento: si se ha cambiado de clinica o de cuenta,
//  NO se manda y se avisa (`otra_sesion`); sale en cuanto se vuelva a entrar con esa cuenta en esa
//  clinica. Y viaja con `X-Envio-Clinica` / `X-Envio-Usuario`, para que el servidor lo compruebe otra
//  vez por su cuenta. Lo de otra sesion no se le enseña a quien esta ahora (ni de que paciente es).
//
//  EL PERIODONTOGRAMA NO PISA LO QUE SE HIZO DESPUES. Un guardado de sus medidas que se quedo en la
//  cola viaja con la version del estudio sobre la que se midio (`base_version`); si el estudio cambio
//  entretanto, el servidor no escribe (409 `cambiado`) y se le pregunta al doctor (`conflicto`). Y de
//  un mismo estudio solo cuenta el ULTIMO guardado: uno nuevo sustituye al pendiente.
//
//  t545 (29-oct-2026) · DE UN MISMO ESTUDIO, UNA SOLA ENTRADA, PASE LO QUE PASE. El dueño vio DIEZ
//  veces «Medidas del periodontograma · estudio 22781 · No se ha podido mandar: Estudio no encontrado».
//  Cada guardado automatico (800 ms despues de cada casilla) solo sustituia a los «pendiente»: los que
//  ya habian fallado, chocado o iban de camino se quedaban, y otra pestaña cogia el que esta mandaba
//  en directo y lo apuntaba como fallido. Ahora (1) un guardado nuevo FUNDE todos los de ese estudio que
//  no esten en la red (manda el ultimo: lleva el estudio ENTERO, porque el servidor reemplaza `datos`),
//  y al repartir la cola se funden tambien los que quedaron de antes; (2) de un mismo estudio va UNO a
//  la vez y por orden, y uno que iba de camino y falla cuando ya hay otro mas nuevo, sobra; (3) otra
//  pestaña no coge lo que esta va mandando; (4) un 404 o un 403 son definitivos (`esDefinitivo`): se
//  dicen una vez, no se reintentan solos y lo medido se conserva para guardarlo en un estudio nuevo.
//
//  EL MOTOR NO SABE NADA DEL NAVEGADOR: el almacen, la red, la sesion y el reloj entran por parametro,
//  para poder medirlo con el servidor caido y vuelto a levantar (backend/routes/cola-envios-caida.test.js).
// ══════════════════════════════════════════════════════════════════════════════════════════════

import { t as i18nT } from './i18n.js'   // B-2259 · los textos, en el idioma de quien mira (utils/i18n)

export const DIAS_MAX = 7
export const ESPERAS_MS = [5000, 15000, 30000, 60000, 120000, 300000]
// t545 · Lo que puede tardar como mucho una peticion (services/api: 120 s) y un margen. Un «enviando» mas nuevo que esto
// lo puede estar mandando OTRA pestaña de este ordenador (comparten el almacen): no se le quita de las manos. Uno mas viejo
// es de una pagina que se cerro o se recargo a mitad, y vuelve a pendiente.
export const ENVIANDO_MAX_MS = 150000

// t545 · LO QUE NO SE ARREGLA REINTENTANDO. Un 404 (el estudio o la cita ya no existen, o ya no se tiene acceso a ese
// paciente) o un 403 (falta el permiso) dan lo mismo la vez cien que la primera: se dice UNA vez, con su motivo, y no
// se vuelve a mandar solo. Lo medido NO se tira: se queda para «Guardarlas en un estudio nuevo» o copiarlo.
export function esDefinitivo(status) {
  return status === 403 || status === 404 || status === 410
}

// t545 · LO QUE DEJO LA COLA DE ANTES. Hasta este arreglo un 404 del periodontograma quedaba como «No se ha podido
// mandar: Estudio no encontrado», sin marcar como definitivo y con «Reintentar». Es el mismo 404: se marca como tal.
export function normalizarViejo(e) {
  if (e && e.estado === 'fallido' && !e.definitivo && e.tipo === 'perio_datos' && /no encontrad/i.test(String(e.error || ''))) {
    e.definitivo = true; e.status = 404; e.motivo = e.motivo || 'no_existe'
    return true
  }
  return false
}

// Que respuesta quiere decir «el servidor no ha podido ahora; vuelve a intentarlo»: sin respuesta, sesion caducada
// (sale al volver a entrar), saturado, o un 5xx que NO es una respuesta razonada de la app (la pagina de error del
// proxy cuando la app se reinicia o esta caida). Un 5xx de la app CON su `motivo` (la IA no contesto, falta una
// migracion) es una respuesta: reintentarlo solo repetiria el gasto y el fallo, y se le dice a quien lo mando.
export function esReintentable(status, data) {
  if (!status || status === 401 || status === 408 || status === 425 || status === 429) return true
  if (status >= 500) return !(data && typeof data === 'object' && data.motivo)
  return false
}

// ── EL ALMACEN DEL DISPOSITIVO ──────────────────────────────────────────────────────────────
// IndexedDB guarda tal cual los Blob del audio. Sin IndexedDB (navegador privado muy restrictivo), una
// cola EN MEMORIA: sigue reintentando mientras la pagina este abierta, y el aviso lo dice.
export function almacenEnMemoria() {
  const m = new Map()
  return {
    persistente: false,
    async todas() { return Array.from(m.values()).map((e) => ({ ...e })) },
    async poner(e) { m.set(e.id, { ...e }) },
    async quitar(id) { m.delete(id) },
  }
}

export function almacenIndexedDB(idb, nombre = 'biango-cola-envios') {
  if (!idb || typeof idb.open !== 'function') return null
  let _db = null
  const abrir = () => _db || (_db = new Promise((ok, ko) => {
    const r = idb.open(nombre, 1)
    r.onupgradeneeded = () => { const db = r.result; if (!db.objectStoreNames.contains('envios')) db.createObjectStore('envios', { keyPath: 'id' }) }
    r.onsuccess = () => ok(r.result)
    r.onerror = () => { _db = null; ko(r.error) }
  }))
  const tx = async (modo, fn) => {
    const db = await abrir()
    return new Promise((ok, ko) => {
      const t = db.transaction('envios', modo)
      const res = fn(t.objectStore('envios'))
      t.oncomplete = () => ok(res && 'result' in res ? res.result : undefined)
      t.onerror = () => ko(t.error)
      t.onabort = () => ko(t.error)
    })
  }
  return {
    persistente: true,
    async todas() { return (await tx('readonly', (s) => s.getAll())) || [] },
    async poner(e) { await tx('readwrite', (s) => s.put(e)) },
    async quitar(id) { await tx('readwrite', (s) => s.delete(id)) },
  }
}

const UUID_FORMA = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/

/**
 * @param {object} o
 *  almacen   { todas, poner, quitar } (almacenIndexedDB(...) o almacenEnMemoria())
 *  red       async ({ metodo, url, json, archivo, campos, cabeceras }) → { status, data }; LANZA si no hay respuesta
 *  sesion()  → { usuarioId, clinicaId } de AHORA, o null sin sesion
 *  uuid()    identificador nuevo
 *  ahora()   reloj (ms)
 *  bloqueo   async (fn) → que solo una pestaña reparta la cola a la vez (navigator.locks), opcional
 */
export function crearCola({ almacen, red, sesion, uuid, ahora = () => Date.now(), bloqueo = null }) {
  const oyentes = new Set()
  const alEnviar = new Set()
  const enVuelo = new Set()    // ids que ESTA pestaña esta mandando ahora (no se mandan dos veces a la vez)
  const enCamino = new Set()   // t545 · de esos, los que ya van por la red (los demas esperan su turno y se pueden fundir)
  const turnos = new Map()     // t545 · clave → el envio de ese destino que va delante: de un mismo estudio, uno a la vez
  const ultimoOk = new Map()   // clave → creado_ms del ultimo envio de esa clave que llego
  let lista = []
  let procesando = null

  const misma = (e, s) => !!s && Number(e.usuarioId) === Number(s.usuarioId) && Number(e.clinicaId) === Number(s.clinicaId)
  // t545 · ¿lo esta mandando OTRA pestaña ahora mismo? (un «enviando» reciente que esta pestaña no lleva)
  const enOtraPestana = (x) => x.estado === 'enviando' && !enVuelo.has(x.id) && ahora() - (x.enviando_ms || 0) < ENVIANDO_MAX_MS
  // t545 · ¿se puede fundir con uno mas nuevo? Todo lo que no va por la red: pendiente, esperando turno, fallido,
  // definitivo o en conflicto (ninguno llego al servidor: los que llegan salen de la cola).
  const fundible = (x) => !enCamino.has(x.id) && !enOtraPestana(x)
  const masNuevoQue = (e) => !!e.clave && lista.some((x) => x.id !== e.id && x.clave === e.clave && misma(x, e) && x.creado_ms > e.creado_ms)
  const avisar = () => { for (const f of [...oyentes]) { try { f(resumen()) } catch (_) { /* nada */ } } }
  async function recargar() { lista = (await almacen.todas()).sort((a, b) => a.creado_ms - b.creado_ms); avisar(); return lista }

  // Lo que ve la persona que esta en sesion: solo LO SUYO con detalle; de lo de otras sesiones, solo cuantos hay.
  function resumen() {
    const s = (() => { try { return sesion() } catch (_) { return null } })()
    const mias = lista.filter((e) => misma(e, s))
    return {
      pendientes: mias.filter((e) => e.estado === 'pendiente' || e.estado === 'enviando').length,
      conflictos: mias.filter((e) => e.estado === 'conflicto').length,
      fallidos: mias.filter((e) => e.estado === 'fallido').length,
      deOtraSesion: s ? lista.filter((e) => !misma(e, s)).length : 0,
      persistente: almacen.persistente !== false,
      mias: mias.map(({ archivo, json, ...e }) => ({ ...e, tieneAudio: !!(archivo && archivo.blob), tieneMedidas: !!(json && json.datos && typeof json.datos === 'object') })),
    }
  }

  async function guardar(e) { await almacen.poner(e); lista = lista.filter((x) => x.id !== e.id).concat([e]).sort((a, b) => a.creado_ms - b.creado_ms); avisar() }
  async function quitar(id) { await almacen.quitar(id); lista = lista.filter((x) => x.id !== id); avisar() }

  // t545 · FUNDIR: de los `viejos` (todos del mismo destino, misma cuenta y clinica, y mas viejos que `queda`) no queda
  // ninguno. Lo que manda es lo ultimo medido (`queda`): el guardado del periodontograma es el estudio ENTERO (el servidor
  // reemplaza `datos`), asi que lo de antes ya va dentro. La version de partida es la del MAS VIEJO, que es la que de
  // verdad tiene el servidor: ninguno de los fundidos llego (lo que llega sale de la cola).
  async function fundirEn(queda, viejos) {
    let base = null, cuando = Infinity
    for (const v of viejos) if (v.base_version && v.creado_ms < cuando) { base = v.base_version; cuando = v.creado_ms }
    if (base && cuando < queda.creado_ms) queda.base_version = base
    for (const v of viejos) await quitar(v.id)
  }
  // t545 · Y lo que ya estaba repetido en el dispositivo (lo de antes de este arreglo, o lo que dejo otra pestaña): por
  // cada destino de esta sesion, queda el mas nuevo.
  async function fundirTodo(s) {
    const grupos = new Map()
    for (const x of lista) {
      if (!x.clave || !misma(x, s) || !fundible(x)) continue
      if (!grupos.has(x.clave)) grupos.set(x.clave, [])
      grupos.get(x.clave).push(x)
    }
    for (const g of grupos.values()) {
      if (g.length < 2) continue
      g.sort((a, b) => a.creado_ms - b.creado_ms)
      const queda = g[g.length - 1]
      if (enVuelo.has(queda.id)) continue   // espera su turno en esta pestaña: lo funde quien lo guardo
      await fundirEn(queda, g.slice(0, -1))
      await guardar(queda)
    }
  }

  /** Guarda un envio en el dispositivo (ANTES de mandarlo). Devuelve la entrada. Sin sesion, lanza. */
  async function encolar(d) {
    const s = sesion()
    if (!s || !s.usuarioId || !s.clinicaId) throw new Error(i18nT('utils.colaEnvios.sin_sesion_no_se_puede', 'Sin sesión: no se puede guardar el envío.'))
    const id = d.id && UUID_FORMA.test(d.id) ? d.id : uuid()
    // t545 · LA CLINICA DEL DESTINO. La pantalla puede decir en que clinica cargo lo que se esta midiendo (el estudio
    // se abrio estando en una clinica y la sesion cambio por debajo, en otra pestaña): entonces el envio es de ESA
    // clinica y solo sale estando en ella. Sin decirlo, la de la sesion de ahora, como siempre. Solo restringe: la
    // cola nunca manda lo de una clinica estando en otra.
    const cDestino = parseInt(d.clinicaId, 10)
    const pac = parseInt(d.paciente_id, 10)
    const e = {
      id, tipo: d.tipo, metodo: d.metodo || 'POST', url: d.url,
      json: d.json || null, archivo: d.archivo || null, campos: d.campos || null,
      base_version: d.base_version || null, clave: d.clave || null, etiqueta: d.etiqueta || '',
      paciente_id: pac > 0 ? pac : null,
      usuarioId: s.usuarioId, clinicaId: cDestino > 0 ? cDestino : s.clinicaId,
      creado_ms: ahora(), intentos: 0, proximo_ms: 0, estado: 'pendiente', error: null,
    }
    // DE UN MISMO DESTINO SOLO CUENTA EL ULTIMO (las medidas de un estudio). t545: no solo el pendiente: tambien el que
    // fallo, el que choco o el que espera su turno (ver `fundirEn`). El que ya va por la red se queda hasta que conteste:
    // si llega, este va detras con su version; si no, sobra (`masNuevoQue`).
    if (e.clave) {
      await fundirEn(e, lista.filter((x) => x.clave === e.clave && misma(x, e) && fundible(x)))
    }
    await guardar(e)
    return e
  }

  const espera = (n) => ESPERAS_MS[Math.min(Math.max(n - 1, 0), ESPERAS_MS.length - 1)]

  // UN intento de UNA entrada. Devuelve { ok, data } | { pendiente: true } | { ok: false, status, error, motivo }.
  // t545 · DE UN MISMO DESTINO, UNO DETRAS DE OTRO. Si el servidor va lento, el guardado de las 15:58:03 no puede
  // llegar DESPUES del de las 15:58:05 y dejar el estudio con lo de antes: cada envio de una clave espera a que conteste
  // el de delante. Mientras espera se puede fundir con uno mas nuevo, y entonces ya no se manda.
  async function intentar(e, opciones = {}) {
    if (enVuelo.has(e.id)) return { pendiente: true }
    enVuelo.add(e.id)
    let soltar = null, cadena = null
    try {
      if (e.clave) {
        const antes = turnos.get(e.clave)
        const mio = new Promise((ok) => { soltar = ok })
        cadena = (antes || Promise.resolve()).then(() => mio)
        turnos.set(e.clave, cadena)
        if (antes) {
          await antes
          if (!lista.some((x) => x.id === e.id)) return { ok: true, sustituido: true }
        }
      }
      return await intentarUnaVez(e, opciones)
    } finally {
      if (soltar) { soltar(); if (turnos.get(e.clave) === cadena) turnos.delete(e.clave) }
      enVuelo.delete(e.id)
    }
  }
  async function intentarUnaVez(e, { primero = false } = {}) {
    const s = sesion()
    if (!misma(e, s)) {
      if (e.estado !== 'otra_sesion') { e.estado = 'otra_sesion'; await guardar(e) }
      return { pendiente: true, otraSesion: true }
    }
    // Lo de un destino que ya tiene un guardado MAS NUEVO que llego, sobra.
    if (e.clave && (ultimoOk.get(e.clave) || 0) > e.creado_ms) { await quitar(e.id); return { ok: true, sustituido: true } }
    e.estado = 'enviando'
    e.enviando_ms = ahora()   // t545 · desde cuando: otra pestaña no lo coge mientras pueda seguir de camino
    enCamino.add(e.id)
    let r
    try {
      await guardar(e)
      const cabeceras = { 'X-Envio-Id': e.id, 'X-Envio-Clinica': String(e.clinicaId), 'X-Envio-Usuario': String(e.usuarioId) }
      // La version de partida solo viaja en los REINTENTOS: el primer envio, en directo, es el guardado de siempre.
      const json = e.json ? (e.intentos > 0 && e.base_version && !e.forzar ? { ...e.json, base_version: e.base_version } : e.json) : null
      try {
        r = await red({ metodo: e.metodo, url: e.url, json, archivo: e.archivo, campos: e.campos, cabeceras })
      } catch (err) {
        r = { status: 0, data: null }
      }
    } finally { enCamino.delete(e.id) }
    const motivo = r.data && r.data.motivo
    const llego = r.status >= 200 && r.status < 300
    // Descartado a mano mientras iba de camino: si no llego, no se vuelve a guardar.
    if (!llego && !lista.some((x) => x.id === e.id)) return { ok: false, descartado: true, status: r.status }
    if (llego) {
      if (e.clave) ultimoOk.set(e.clave, Math.max(ultimoOk.get(e.clave) || 0, e.creado_ms))
      await quitar(e.id)
      // t545 · Lo que espera detras, de ese mismo estudio, ya parte de lo que acaba de llegar: su version de partida es la
      // nueva (si no, al reintentarlo chocaria con un guardado que es suyo y lo tomaria por un cambio de otro puesto).
      if (e.clave && r.data && r.data.version) {
        for (const x of lista.filter((y) => y.clave === e.clave && misma(y, e) && y.creado_ms > e.creado_ms)) {
          x.base_version = String(r.data.version); await guardar(x)
        }
      }
      // `diferido`: llegó desde la cola, más tarde, y no en el intento que la pantalla estaba esperando.
      for (const f of [...alEnviar]) { try { f(e, r.data, { diferido: !primero }) } catch (_) { /* nada */ } }
      return { ok: true, data: r.data, status: r.status }
    }
    // t545 · Mientras iba de camino se guardo otro MAS NUEVO de ese mismo estudio: lleva todo lo de este, asi que este
    // sobra, falle por lo que falle (no se queda como pendiente, ni como fallido, ni como conflicto). Lo dice el nuevo.
    if (masNuevoQue(e)) { await quitar(e.id); return { pendiente: true, sustituido: true, status: r.status } }
    if (r.status === 409 && motivo === 'otra_sesion') { e.estado = 'otra_sesion'; await guardar(e); return { pendiente: true, otraSesion: true } }
    if (r.status === 409 && motivo === 'cambiado') {
      // Si ya llego un guardado mas nuevo de ese mismo estudio, este sobra: no hay nada que preguntar.
      if (e.clave && (ultimoOk.get(e.clave) || 0) > e.creado_ms) { await quitar(e.id); return { ok: true, sustituido: true } }
      e.estado = 'conflicto'; e.error = (r.data && r.data.error) || i18nT('utils.colaEnvios.ha_cambiado_mientras_tanto', 'Ha cambiado mientras tanto.')
      await guardar(e)
      return { ok: false, conflicto: true, status: 409, error: e.error, motivo }
    }
    if (esReintentable(r.status, r.data) || (r.status === 409 && motivo === 'en_curso')) {
      e.intentos += 1
      e.estado = 'pendiente'
      e.proximo_ms = ahora() + espera(e.intentos)
      e.error = null
      await guardar(e)
      return { pendiente: true, status: r.status }
    }
    // Un NO del servidor (permiso, cita borrada, audio vacio…): reintentarlo no lo arregla.
    const error = (r.data && r.data.error) || ('Error ' + r.status)
    // t545 · Y si es un «no existe» o un «no tienes acceso», es DEFINITIVO: no se vuelve a mandar solo y se dice una vez.
    // Lo medido de un estudio (lo que lleva `clave`) NO se tira aunque sea el primer intento: la pantalla lo esta viendo,
    // pero si se cambia de estudio o se cierra, lo unico que queda de esas medidas es esto.
    if (esDefinitivo(r.status) && !(primero && !e.clave)) {
      e.estado = 'fallido'; e.error = error; e.definitivo = true; e.status = r.status
      e.motivo = motivo || (r.status === 403 ? 'sin_acceso' : 'no_existe'); e.proximo_ms = 0
      await guardar(e)
      return { ok: false, status: r.status, error, motivo: e.motivo, definitivo: true }
    }
    if (primero) { await quitar(e.id); return { ok: false, status: r.status, error, motivo } }   // lo esta viendo: se le dice ya
    e.estado = 'fallido'; e.error = error
    await guardar(e)
    return { ok: false, status: r.status, error, motivo }
  }

  /** Reparte lo pendiente (solo lo de ESTA sesion, y por orden). */
  function procesar() {
    if (procesando) return procesando
    const vuelta = async () => {
      await recargar()
      const s = sesion()
      if (!s) return   // sin sesion (cerrada, caducada, la tablet del paciente): no se manda NADA, se espera
      // t545 · lo que quedo de antes, a su sitio: los «no encontrado» viejos son definitivos (no se vuelven a mandar), y
      // de un mismo estudio queda UNA entrada (la ultima medida), no diez.
      for (const e of lista.filter((x) => misma(x, s))) if (normalizarViejo(e)) await guardar(e)
      await fundirTodo(s)
      for (const e of [...lista]) {
        if (enVuelo.has(e.id)) continue   // ya va de camino desde esta misma pestaña
        if (e.estado === 'otra_sesion' && misma(e, s)) { e.estado = 'pendiente'; e.proximo_ms = 0; await guardar(e) }
        // Lo de otra sesion que lleva mas de DIAS_MAX dias en este dispositivo se va: datos de salud de otra
        // persona no se guardan indefinidamente en un ordenador compartido. Lo propio NO se borra solo.
        if (!misma(e, s) && ahora() - e.creado_ms > DIAS_MAX * 86400000) { await quitar(e.id); continue }
        if (e.estado === 'enviando' && !misma(e, s)) continue
        if (misma(e, s) && e.estado === 'pendiente' && ahora() - (e.desde_ms || e.creado_ms) > DIAS_MAX * 86400000) {
          e.estado = 'fallido'; e.error = i18nT('utils.colaEnvios.lleva_mas_de_dias_sin', 'Lleva más de {{DIAS_MAX}} días sin poder mandarse.', { DIAS_MAX }); await guardar(e); continue
        }
        // Un «enviando» que se quedo asi (la pagina se cerro a mitad) vuelve a pendiente. t545: pero no uno reciente, que lo
        // puede estar mandando OTRA pestaña en directo (antes se le cogia, se mandaba dos veces y, si el servidor decia que
        // no, esta lo apuntaba como fallido aunque la otra ya lo hubiera dicho y quitado: una entrada mas por guardado).
        if (e.estado === 'enviando') {
          if (enOtraPestana(e)) continue
          e.estado = 'pendiente'; await guardar(e)
        }
        if (e.estado !== 'pendiente' && e.estado !== 'otra_sesion') continue
        if ((e.proximo_ms || 0) > ahora()) continue
        await intentar(e)
      }
    }
    procesando = (bloqueo ? bloqueo(vuelta) : vuelta()).catch(() => {}).finally(() => { procesando = null })
    return procesando
  }

  /**
   * Guardar y mandar YA: lo que usan las pantallas. Devuelve lo que paso en ESTE intento:
   *  { ok: true, data }   llego (data = la respuesta de siempre)
   *  { pendiente: true }  guardado en el dispositivo; se mandara solo
   *  { ok: false, error } el servidor dijo que no (se le dice a quien lo esta mirando)
   */
  async function enviar(d) {
    let e
    try { e = await encolar(d) } catch (err) {
      // Sin donde guardarlo: se manda como siempre, sin cola (y sin red, falla como siempre).
      const r = await red({ metodo: d.metodo || 'POST', url: d.url, json: d.json, archivo: d.archivo, campos: d.campos, cabeceras: {} })
        .catch(() => ({ status: 0, data: null }))
      return r.status >= 200 && r.status < 300 ? { ok: true, data: r.data } : { ok: false, status: r.status, error: (r.data && r.data.error) || i18nT('utils.colaEnvios.no_se_ha_podido_enviar', 'No se ha podido enviar.') }
    }
    const res = await intentar(e, { primero: true })
    return res
  }

  return {
    encolar, enviar, procesar, recargar, resumen,
    suscribir(f) { oyentes.add(f); try { f(resumen()) } catch (_) { /* nada */ } return () => oyentes.delete(f) },
    alEnviarse(f) { alEnviar.add(f); return () => alEnviar.delete(f) },
    /** Lo que esta en la cola para ESTA sesion y ese destino (p. ej. las medidas pendientes de un estudio). */
    deClave(clave) { const s = sesion(); return lista.filter((e) => e.clave === clave && misma(e, s)) },
    async descartar(id) { const s = sesion(); const e = lista.find((x) => x.id === id); if (e && misma(e, s)) await quitar(id) },
    /** t545 · Se ha borrado ese estudio (o esa cita) desde ESTA pantalla: lo que quedaba de el en el dispositivo, de esta
     *  sesion, sobra (`destino` = '/periodonto/123': sus medidas y sus dictados). Lo que ya va por la red, se deja. */
    async descartarDestino(destino) {
      const s = sesion(); const d = String(destino || '')
      if (!d) return 0
      const fuera = lista.filter((x) => misma(x, s) && !enCamino.has(x.id) && (x.url === d || String(x.url || '').startsWith(d + '/')))
      for (const x of fuera) await quitar(x.id)
      return fuera.length
    },
    async reintentar(id) {
      const s = sesion(); const e = lista.find((x) => x.id === id)
      if (!e || !misma(e, s)) return
      // A mano, una vez: tambien lo definitivo (p. ej. el estudio se recupero de la papelera). Solo nunca lo hace sola.
      e.estado = 'pendiente'; e.proximo_ms = 0; e.error = null; e.desde_ms = ahora()
      e.definitivo = false; e.status = null; e.motivo = null
      await guardar(e); return procesar()
    },
    /** t545 · Las medidas guardadas de un envio de ESTA sesion (para copiarlas o guardarlas en un estudio nuevo). */
    medidasDe(id) {
      const s = sesion(); const e = lista.find((x) => x.id === id)
      if (!e || !misma(e, s) || !e.json || !e.json.datos || typeof e.json.datos !== 'object') return null
      return { id: e.id, url: e.url, datos: e.json.datos, paciente_id: e.paciente_id || null, etiqueta: e.etiqueta || '',
        creado_ms: e.creado_ms, usuarioId: e.usuarioId, clinicaId: e.clinicaId }
    },
    /** Conflicto del periodontograma: «usar las mias igualmente» (sin comprobar la version). */
    async forzar(id) {
      const s = sesion(); const e = lista.find((x) => x.id === id)
      if (!e || !misma(e, s)) return null
      e.forzar = true; e.estado = 'pendiente'; e.proximo_ms = 0; e.error = null
      await guardar(e)
      return intentar(e)
    },
    /** El audio guardado, para descargarlo si no se puede mandar. Solo lo de esta sesion. */
    audioDe(id) { const s = sesion(); const e = lista.find((x) => x.id === id); return e && misma(e, s) && e.archivo ? e.archivo : null },
  }
}

// ── COMO LE HABLAN LAS PANTALLAS A LA COLA: POR EVENTOS DE LA VENTANA ─────────────────────────────
// La cola de verdad (services/colaEnvios.js) la monta App.jsx al cargar y escucha estos eventos. Las pantallas que
// mandan (la nota de voz de la cita, el periodontograma) solo importan este util: sin la cola montada (las guardias
// que pintan esas pantallas, o un navegador sin ventana) `enviarConCola` contesta `{ sinCola: true }` en el acto y la
// pantalla manda como siempre, sin cola. Los eventos son SINCRONOS (dispatchEvent): se sabe al momento si alguien atiende.
export const EVENTO_ENVIAR = 'biango:cola-enviar'
export const EVENTO_CAMBIO = 'biango:cola-cambio'
export const EVENTO_ENVIADO = 'biango:cola-enviado'
export const EVENTO_ACCION = 'biango:cola-accion'

const hayVentana = () => typeof window !== 'undefined' && typeof window.dispatchEvent === 'function' && typeof CustomEvent === 'function'

/** Guardar en el dispositivo y mandar. Resuelve como `cola.enviar` o con `{ sinCola: true }`. */
export function enviarConCola(envio) {
  return new Promise((resolver) => {
    if (!hayVentana()) { resolver({ sinCola: true }); return }
    let atendido = false
    const detail = { envio, atender() { atendido = true }, responder: resolver }
    try { window.dispatchEvent(new CustomEvent(EVENTO_ENVIAR, { detail })) } catch (_) { atendido = false }
    if (!atendido) resolver({ sinCola: true })
  })
}

/** Escuchar la cola: `alCambiar(resumen)` y `alEnviarse({ tipo, url, clave }, respuesta, { diferido })`. Devuelve cómo dejar de escuchar. */
export function escucharCola({ alCambiar, alEnviarse } = {}) {
  if (!hayVentana() || typeof window.addEventListener !== 'function') return () => {}
  const c = (ev) => { try { alCambiar && alCambiar(ev.detail) } catch (_) { /* nada */ } }
  const en = (ev) => { try { const d = ev.detail || {}; alEnviarse && alEnviarse(d.envio, d.data, { diferido: !!d.diferido }) } catch (_) { /* nada */ } }
  window.addEventListener(EVENTO_CAMBIO, c)
  window.addEventListener(EVENTO_ENVIADO, en)
  // Lo de AHORA, sin esperar al siguiente cambio.
  try { window.dispatchEvent(new CustomEvent(EVENTO_ACCION, { detail: { accion: 'avisar' } })) } catch (_) { /* nada */ }
  return () => { window.removeEventListener(EVENTO_CAMBIO, c); window.removeEventListener(EVENTO_ENVIADO, en) }
}

/** Una accion sobre un envio de ESTA sesion: 'forzar' | 'descartar' | 'reintentar'; o 'descartarDestino' con la direccion
 *  ('/periodonto/123') en vez del id: lo que queda en el dispositivo de un estudio que se acaba de borrar (t545). */
export function accionCola(accion, id) {
  if (!hayVentana()) return
  try { window.dispatchEvent(new CustomEvent(EVENTO_ACCION, { detail: { accion, id } })) } catch (_) { /* nada */ }
}

/** El puente: lo monta services/colaEnvios.js con la cola de verdad (una vez por pestaña). */
export function conectarCola(cola) {
  if (!hayVentana() || typeof window.addEventListener !== 'function') return () => {}
  const enviar = (ev) => {
    const d = ev.detail || {}
    if (typeof d.atender !== 'function') return
    d.atender()
    Promise.resolve().then(() => cola.enviar(d.envio)).then((r) => d.responder(r), (err) => d.responder({ ok: false, error: (err && err.message) || i18nT('utils.colaEnvios.no_se_ha_podido_enviar', 'No se ha podido enviar.') }))
  }
  const accion = (ev) => {
    const d = ev.detail || {}
    if (d.accion === 'avisar') { const r = cola.resumen(); window.dispatchEvent(new CustomEvent(EVENTO_CAMBIO, { detail: r })); return }
    if (d.accion === 'forzar') cola.forzar(d.id)
    else if (d.accion === 'descartar') cola.descartar(d.id)
    else if (d.accion === 'reintentar') cola.reintentar(d.id)
    else if (d.accion === 'descartarDestino' && typeof cola.descartarDestino === 'function') cola.descartarDestino(d.id)
  }
  window.addEventListener(EVENTO_ENVIAR, enviar)
  window.addEventListener(EVENTO_ACCION, accion)
  const quitar1 = cola.suscribir((r) => { try { window.dispatchEvent(new CustomEvent(EVENTO_CAMBIO, { detail: r })) } catch (_) { /* nada */ } })
  const quitar2 = cola.alEnviarse((e, data, info) => {
    const { archivo, json, ...envio } = e
    try { window.dispatchEvent(new CustomEvent(EVENTO_ENVIADO, { detail: { envio, data, diferido: !!(info && info.diferido) } })) } catch (_) { /* nada */ }
  })
  return () => { window.removeEventListener(EVENTO_ENVIAR, enviar); window.removeEventListener(EVENTO_ACCION, accion); quitar1(); quitar2() }
}
